import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Clock } from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { brl } from "../../lib/format";
import { useI18n } from "../../i18n/I18nContext";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const { t } = useI18n();
  const map = {
    agendado: ["Agendado", "bg-amber-50 text-amber-800 border-amber-200"],
    em_andamento: ["Em andamento", "bg-blue-50 text-blue-800 border-blue-200"],
    concluido: ["Concluído", "bg-emerald-50 text-emerald-800 border-emerald-200"],
    cancelado: ["Cancelado", "bg-rose-50 text-rose-800 border-rose-200"],
  };
  const [label, cls] = map[status] || [status || "—", "bg-slate-100 text-slate-700 border-slate-200"];
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {t(label)}
    </span>
  );
}

export default function PortalAgenda() {
  const { t } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await portalApi.get("/portal/jobs");
        setJobs(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-slate-500 text-sm">{t("Carregando…")}</div>;

  if (jobs.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="portal-agenda-empty">
        {t("Nenhum serviço cadastrado ainda.")}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="portal-agenda">
      <h1 className="text-2xl font-bold text-slate-900">{t("Minha Agenda")}</h1>

      <ul className="space-y-3">
        {jobs.map((j) => (
          <li
            key={j.id}
            data-testid={`portal-job-${j.id}`}
            className="bg-white rounded-2xl border border-slate-200 p-4 hover:border-blue-300 transition-colors"
          >
            <div className="flex flex-wrap items-start gap-3 justify-between">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Calendar size={14} className="text-blue-600" />
                  <span className="font-semibold">{fmt(j.scheduled_at)}</span>
                </div>
                <div className="text-sm text-slate-600 flex items-center gap-1">
                  <MapPin size={13} /> {j.address || "—"}
                </div>
                {j.finished_at && (
                  <div className="text-xs text-emerald-700 flex items-center gap-1">
                    <Clock size={11} /> {t("finalizado em")} {fmt(j.finished_at)}
                  </div>
                )}
              </div>
              <div className="text-right space-y-1">
                <StatusPill status={j.status} />
                {j.value ? (
                  <div className="text-sm font-bold text-slate-900">{brl(j.value)}</div>
                ) : null}
              </div>
            </div>
            {j.observations && (
              <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-line">
                {j.observations}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
